import React from "react";
import { useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import Input from "components/ui/input/Input";
import Label from "components/ui/input/Label";
import Modal from "components/ui/modal/Modal";
import toast from "components/ui/toast/Toast";
import { useUpdateBuyerList } from "lib/hooks/api/buyer_lists/useUpdateBuyerList";

export type RenameBuyerListModalProps = {
  isOpen: boolean;
  onClose: () => void;
  list: { id: string; name: string };
};

export function RenameBuyerListModal({ isOpen, onClose, list }: RenameBuyerListModalProps) {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues: { name: list.name } });
  const { isLoading, mutate } = useUpdateBuyerList({
    onSuccess: () => {
      toast.success("Buyer list updated successfully!");
      onClose();
    },
    onError() {
      toast.error("Failed to update buyer list");
    },
  });

  return (
    <Modal open={isOpen} onOpenChange={(open) => !open && onClose()} width="520px" padding={24}>
      <ModalTitle h2>Rename buyer list</ModalTitle>
      <Form onSubmit={handleSubmit((v) => mutate({ id: list.id, name: v.name }))}>
        <Flex column gap="4px">
          <Label htmlFor="name">List name</Label>
          <Input
            name="name"
            control={control}
            rules={{ required: "List name is required" }}
            placeholder="Enter list name"
          />
          {errors.name && <ErrorText>{errors.name.message}</ErrorText>}
        </Flex>
        <Flex justifyContent="flex-end" gap="8px">
          <Button buttonType="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" loading={isLoading}>
            {isLoading ? "Updating..." : "Rename buyer list"}
          </Button>
        </Flex>
      </Form>
    </Modal>
  );
}

export default RenameBuyerListModal;

const ModalTitle = styled(Text)({
  margin: "0 0 16px",
});

const Form = styled.form({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
});

const ErrorText = styled.span(({ theme }) => ({
  fontSize: "12px",
  color: theme.colors.sysDestructiveDefault,
}));
